import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { format, parseISO } from 'date-fns';
import ReportLayout, { ReportTable, usePagination } from './ReportLayout';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import clsx from 'clsx';

const fmtDate = v => { try { return v ? format(parseISO(v), 'd MMM yyyy') : '–'; } catch { return v || '–'; } };

const yesNoBadge = v =>
  v
    ? <span className="px-1.5 rounded-full bg-green-100 text-green-700 text-xs font-medium">Yes</span>
    : <span className="px-1.5 rounded-full bg-slate-100 text-slate-500 text-xs font-medium">No</span>;

const COLS = [
  { key: 'client_name',                  label: 'Client Name' },
  { key: 'ndis_number',                  label: 'NDIS Number',      render: v => v || '–' },
  {
    key: 'plan_period_end',
    label: 'Plan Expiry',
    render: v => {
      if (!v) return '–';
      const past = new Date(v) < new Date();
      return <span className={clsx('font-medium', past ? 'text-red-600' : '')}>{fmtDate(v)}</span>;
    },
  },
  { key: 'docs_total',                   label: 'Docs Total',       render: v => v || 0 },
  {
    key: 'docs_expired',
    label: 'Docs Expired',
    render: v => <span className={v > 0 ? 'font-bold text-red-600' : 'text-slate-500'}>{v || 0}</span>,
  },
  {
    key: 'docs_expiring_soon',
    label: 'Expiring Soon',
    render: v => <span className={v > 0 ? 'font-bold text-amber-600' : 'text-slate-500'}>{v || 0}</span>,
  },
  { key: 'active_goals',                 label: 'Active Goals',     render: v => v || 0 },
  { key: 'completed_goals',              label: 'Completed Goals',  render: v => v || 0 },
  { key: 'has_active_care_plan',         label: 'Care Plan',        render: yesNoBadge },
  { key: 'has_active_service_agreement', label: 'Service Agreement', render: yesNoBadge },
];

export default function ClientChecklistReport() {
  const [data, setData]               = useState([]);
  const [loading, setLoading]         = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [search, setSearch]           = useState('');

  const filtered = useMemo(() =>
    search
      ? data.filter(r => (r.client_name || '').toLowerCase().includes(search.toLowerCase()))
      : data,
    [data, search]
  );

  const pagination = usePagination(filtered);

  const load = useCallback(() => {
    setLoading(true);
    api.get('/reports/client-checklist')
      .then(r => { setData(r.data || []); pagination.reset(); })
      .catch(() => toast.error('Failed to load'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const summary = {
    total:           data.length,
    expiredDocs:     data.filter(r => parseInt(r.docs_expired) > 0).length,
    missingCarePlan: data.filter(r => !r.has_active_care_plan).length,
    activeSA:        data.filter(r => r.has_active_service_agreement).length,
  };

  const chips = [
    ...(search ? [{ label: 'Search', value: search, onRemove: () => setSearch('') }] : []),
  ];

  return (
    <ReportLayout
      title="Client Checklist Report"
      data={filtered}
      filename="client_checklist_report"
      columns={COLS}
      activeChips={chips}
      showFiltersPanel={showFilters}
      onToggleFilters={() => setShowFilters(v => !v)}
      filters={
        <div className="flex flex-wrap gap-4">
          <div>
            <label className="block text-xs font-semibold text-slate-500 mb-1">Search by Name</label>
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Client name..."
              className="input-field !w-auto text-sm"
            />
          </div>
          <div className="flex items-end text-sm text-slate-500">
            Shows all active clients — filtered client-side by name.
          </div>
        </div>
      }
    >
      <div className="grid grid-cols-4 gap-4 mb-4">
        {[
          { label: 'Total Clients',             value: summary.total,            color: 'text-slate-900' },
          { label: 'Clients with Expired Docs', value: summary.expiredDocs,      color: 'text-red-600'   },
          { label: 'Missing Care Plan',          value: summary.missingCarePlan,  color: 'text-amber-600' },
          { label: 'Clients with Active SA',     value: summary.activeSA,         color: 'text-green-600' },
        ].map(card => (
          <div key={card.label} className="bg-white border border-slate-200 rounded-xl p-4">
            <div className="text-xs text-slate-500 mb-1">{card.label}</div>
            <div className={clsx('text-2xl font-bold', card.color)}>{card.value}</div>
          </div>
        ))}
      </div>
      <ReportTable
        columns={COLS}
        data={filtered}
        loading={loading}
        pagination={pagination}
        emptyMsg="No active clients found."
      />
    </ReportLayout>
  );
}
